// 流式问答客户端（FR-2/3.3, NFR 4.2）。
// 用 fetch + ReadableStream 手写 SSE：可读 429 状态（防刷提示）、可带 Last-Event-ID
// 静默重连（后端按 conv_id 回放缓冲，不重复计费）、可随时 abort。
import { enc } from "./api";
import type { ChatMeta } from "./types";

export interface StreamParams {
  question: string;
  userId: string;
  sessionId: string;
  convId: string;
  script: string;
}

export type StreamStatus = "open" | "reconnecting" | "rate_limited" | "error";

export interface StreamHandlers {
  onMeta?: (m: ChatMeta) => void;
  onDelta?: (text: string) => void;
  onDone?: () => void;
  onStatus?: (s: StreamStatus) => void;
}

interface ParsedEvent {
  event: string;
  id?: string;
  data: string;
}

const MAX_ATTEMPTS = 6;

export class ChatStream {
  private aborted = false;
  private ctrl: AbortController | null = null;
  private lastEventId = -1;

  constructor(private params: StreamParams, private h: StreamHandlers) {}

  abort(): void {
    this.aborted = true;
    this.ctrl?.abort();
  }

  async run(): Promise<void> {
    let attempt = 0;
    while (!this.aborted) {
      try {
        const result = await this.connectOnce();
        if (result === "done" || this.aborted) return;
        // 服务器无 done 即关闭：当作可重连
      } catch (err) {
        if ((err as Error)?.name === "RateLimitError") {
          this.h.onStatus?.("rate_limited");
          return;
        }
        if (this.aborted) return;
      }
      if (this.aborted) return;
      attempt += 1;
      if (attempt > MAX_ATTEMPTS) {
        this.h.onStatus?.("error");
        return;
      }
      this.h.onStatus?.("reconnecting");
      await delay(Math.min(3000, 400 * attempt)); // 首次 ~0.4s，封顶 3s
    }
  }

  private buildUrl(): string {
    const p = this.params;
    return (
      `/api/chat/stream?question=${enc(p.question)}` +
      `&user_id=${enc(p.userId)}&session_id=${enc(p.sessionId)}` +
      `&conv_id=${enc(p.convId)}&script=${enc(p.script)}`
    );
  }

  private async connectOnce(): Promise<"done" | "closed"> {
    this.ctrl = new AbortController();
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (this.lastEventId >= 0) headers["Last-Event-ID"] = String(this.lastEventId);

    const resp = await fetch(this.buildUrl(), { headers, signal: this.ctrl.signal });
    if (resp.status === 429) {
      const e = new Error("rate-limited");
      e.name = "RateLimitError";
      throw e;
    }
    if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status}`);
    this.h.onStatus?.("open");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buf = "";

    for (;;) {
      const { value, done } = await reader.read();
      if (done) return "closed";
      buf += decoder.decode(value, { stream: true });
      buf = buf.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

      let sep: number;
      while ((sep = buf.indexOf("\n\n")) >= 0) {
        const block = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const ev = parseEvent(block);
        if (!ev) continue;

        if (ev.id !== undefined) {
          const n = parseInt(ev.id, 10);
          if (!Number.isNaN(n)) {
            if (n <= this.lastEventId) continue; // 重连回放去重
            this.lastEventId = n;
          }
        }

        if (ev.event === "meta") {
          try {
            this.h.onMeta?.(JSON.parse(ev.data) as ChatMeta);
          } catch {
            /* ignore malformed meta */
          }
        } else if (ev.event === "delta") {
          this.h.onDelta?.(ev.data);
        } else if (ev.event === "done") {
          this.ctrl = null;
          this.h.onDone?.();
          return "done";
        }
      }
    }
  }
}

function parseEvent(block: string): ParsedEvent | null {
  let event = "message";
  let id: string | undefined;
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue; // 注释行（如 ": open"）
    const i = line.indexOf(":");
    const field = i < 0 ? line : line.slice(0, i);
    let value = i < 0 ? "" : line.slice(i + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "id") id = value;
    else if (field === "data") dataLines.push(value);
  }
  if (!dataLines.length && event === "message") return null;
  return { event, id, data: dataLines.join("\n") };
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
